import { observer } from "mobx-react";
import styles from "./styles/NewsBlock.module.scss";
import BlockTitle from "../_BlockTitle";
import { Suspense, use, useMemo, useState } from "react";
import {
  IReactiveNewsContentItem,
  ReactiveContentItem,
} from "../../../schoolfeed/SchoolFeedContentItem";
import classNames from "classnames";
import { EditDropDownItem } from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import { SupportedContentItemTypes } from "../../../schoolfeed/PostManager/SchoolFeedPostManager";
import { useCreateContentItem } from "../../../../hooks/useCreateContentItem";
import NewsSelector from "./NewsSelector";
import { Toggle } from "../../SchoolBlocks/EditBlockComponents/EditBlockInputs/Toggle";
import { ITerraceProps } from "../Terrace";
import { useStores } from "../../../../stores/useStores";
import { useSchoolFeedInfiniteScroll } from "../../../schoolfeed/hooks/useSchoolFeedInfiniteScroll";
import { FetchMoreNewsButton } from "./FetchMoreNewsButton";
import { SchoolFeedNewsLoading } from "../../../schoolfeed/SchoolFeedNewsDisplay";
import { useNewsTerraceBlockPromise } from "../../../../hooks/useNewsTerraceBlockPromise";

type IReactiveNewsTerraceObj = Omit<INewsTerrace, "content_data">;

type OrganizationContentData = {
  title: string;
  items: IReactiveNewsContentItem[];
  page_size: number;
  total: number;
};

type ContentData = {
  [org_id: string]: OrganizationContentData;
};

type ContentDataPromise = {
  [org_id: string]: Omit<OrganizationContentData, "items"> & {
    items: NewsContentItem[];
  };
};

interface NewsBlockProps extends ITerraceProps {
  blockObj: IReactiveNewsTerraceObj;
  initialBlockPromise: Promise<ContentDataPromise>;
}

const NewsBlock = observer((props: NewsBlockProps) => {
  const { organizationStore, sidebarStore, userStore } = useStores();

  const { handleSave } = useCreateContentItem();

  const showDistrictOnSchoolSite =
    organizationStore.currentOrganization.type === "school" &&
    organizationStore.organization.district;
  const initialContentData = use(props.initialBlockPromise);
  const contentData: ContentData = useMemo(() => {
    return Array.from(Object.entries(initialContentData)).reduce(
      (acc, [org_id, data]) => {
        acc[org_id] = {
          ...data,
          items: data.items.map((item) => new ReactiveContentItem(item)),
        };
        return acc;
      },
      {},
    );
  }, [initialContentData]);
  const contentDataKeys = Array.from(Object.keys(contentData));
  const primaryOrgId = contentDataKeys[0];
  const secondaryOrgId = contentDataKeys.length > 1 ? contentDataKeys[1] : null;

  const [currentOrgId, setCurrentOrgId] = useState<string>(() => {
    if (contentDataKeys.length === 2) {
      if (contentData[primaryOrgId].items.length > 0) {
        return primaryOrgId;
      } else {
        return secondaryOrgId as string;
      }
    } else {
      return primaryOrgId;
    }
  });

  const newsOptions = Object.entries(contentData).map(([org_id, data]) => {
    return {
      value: org_id,
      label: data.title,
    };
  });

  const onClick = () => {
    sidebarStore.setSidebar({
      view: "SchoolFeedPost",
      sbtype: SupportedContentItemTypes.NEWS,
      handleSave: handleSave,
    });
  };

  const newsBlockDropDownItems: EditDropDownItem[] = [
    {
      title: "Add News Post",
      icon: <FontAwesome prefix={"fas"} fixedWidth className="fa-plus" />,
      onClick: onClick,
    },
  ];

  const newsTerraceTitle = () => {
    if (
      organizationStore.organization.type === "district" ||
      showDistrictOnSchoolSite
    ) {
      return "District News";
    } else if (organizationStore.organization.type === "school") {
      return "School News";
    }
  };

  const titleandEditPencilClassName = classNames({
    [styles.titleAndEditPencil]:
      organizationStore.currentOrganization.type !== "district",
  });

  const customTitleComponent =
    currentOrgId === organizationStore.currentOrganization.id ? undefined : (
      <div className={styles.titleContainer}>
        <div
          className={`${styles.editableTitle} ${styles.editableTitleParent}`}
        >
          <h2>{newsTerraceTitle()}</h2>
        </div>
      </div>
    );

  const {
    contentItems: primaryOrgContentItems,
    totalContentItems: primaryOrgTotalContentItems,
    getItemAtIndex: primaryOrgGetItemAtIndex,
    showViewMore: primaryOrgShowViewMore,
    size: primaryOrgSize,
    setSize: primaryOrgSetSize,
    mutate: primaryOrgMutate,
    isLoading: primaryOrgIsLoading,
  } = useSchoolFeedInfiniteScroll(
    "news-terrace",
    {
      organization_pk: primaryOrgId,
      page_size: contentData[primaryOrgId].page_size,
      news_or_third_party_media: true,
      published: !userStore.isEditor,
      organization_id: primaryOrgId,
      expand: "organization",
    },
    {
      contentItems: contentData[primaryOrgId].items,
      total: contentData[primaryOrgId].total,
    },
  );

  const {
    contentItems: secondaryOrgContentItems,
    totalContentItems: secondaryOrgTotalContentItems,
    getItemAtIndex: secondaryOrgGetItemAtIndex,
    showViewMore: secondaryOrgShowViewMore,
    size: secondaryOrgSize,
    setSize: secondaryOrgSetSize,
    mutate: secondaryOrgMutate,
    isLoading: secondaryOrgIsLoading,
  } = useSchoolFeedInfiniteScroll(
    "news-terrace",
    {
      organization_pk: secondaryOrgId,
      page_size: secondaryOrgId ? contentData[secondaryOrgId].page_size : 0,
      news_or_third_party_media: true,
      published: !userStore.isEditor,
      organization_id: secondaryOrgId,
      expand: "organization",
    },
    {
      contentItems: secondaryOrgId ? contentData[secondaryOrgId].items : [],
      total: secondaryOrgId ? contentData[secondaryOrgId].total : 0,
    },
  );

  const isPrimaryOrg = currentOrgId === primaryOrgId;
  const contentItems = isPrimaryOrg
    ? primaryOrgContentItems
    : secondaryOrgContentItems;
  const totalContentItems = isPrimaryOrg
    ? primaryOrgTotalContentItems
    : secondaryOrgTotalContentItems;
  const getItemAtIndex = isPrimaryOrg
    ? primaryOrgGetItemAtIndex
    : secondaryOrgGetItemAtIndex;
  const showViewMore = isPrimaryOrg
    ? primaryOrgShowViewMore
    : secondaryOrgShowViewMore;
  const size = isPrimaryOrg ? primaryOrgSize : secondaryOrgSize;
  const setSize = isPrimaryOrg ? primaryOrgSetSize : secondaryOrgSetSize;
  const mutate = isPrimaryOrg ? primaryOrgMutate : secondaryOrgMutate;
  const isLoading = isPrimaryOrg ? primaryOrgIsLoading : secondaryOrgIsLoading;

  return (
    <div className={styles.newsBlockContainer}>
      <div id={"blockTitle"} className={titleandEditPencilClassName}>
        <BlockTitle
          blockObj={props.blockObj}
          aboveTheFold={props.aboveTheFold}
          blockTitleContainerClassName={styles.blockTitleContainerClassName}
          dropDownItems={
            currentOrgId === organizationStore.currentOrganization.id
              ? newsBlockDropDownItems
              : undefined
          }
          customTitleComponent={customTitleComponent}
          showViewMore={false}
        />
        {(organizationStore.currentOrganization.type !== "district" ||
          showDistrictOnSchoolSite) && (
          <Toggle
            value={currentOrgId}
            onChange={setCurrentOrgId}
            options={newsOptions}
            className={styles.toggleClassName}
            id={String(props.blockObj.id)}
          />
        )}
      </div>
      {contentItems.length === 0 ? (
        <div className={styles.noContent}>
          No content to display, please try another filter.
        </div>
      ) : (
        <NewsSelector
          contentItems={contentItems as IReactiveNewsContentItem[]}
          aboveTheFold={props.aboveTheFold}
          viewMoreButton={
            showViewMore ? (
              <FetchMoreNewsButton
                onClick={async () => await setSize(size + 1)}
                isLoading={isLoading}
              />
            ) : undefined
          }
          getItemAtIndex={getItemAtIndex}
          totalItemCount={totalContentItems}
          handleDelete={async () => await mutate()}
        />
      )}
    </div>
  );
});

export default function NewsBlockSuspenseWrapper(props) {
  const { organizationStore } = useStores();

  const newsPromise = props.initialBlockPromise ?? useNewsTerraceBlockPromise();

  const titleandEditPencilClassName = classNames({
    [styles.titleAndEditPencil]:
      organizationStore.currentOrganization.type !== "district",
  });

  return (
    <Suspense
      fallback={
        <div>
          <div id={"blockTitle"} className={titleandEditPencilClassName}>
            <BlockTitle
              blockObj={props.blockObj}
              aboveTheFold={props.aboveTheFold}
              blockTitleContainerClassName={styles.blockTitleContainerClassName}
              showViewMore={false}
            />
          </div>
          <SchoolFeedNewsLoading />
        </div>
      }
    >
      <NewsBlock {...props} initialBlockPromise={newsPromise} />
    </Suspense>
  );
}
